import dayjs from 'dayjs'
import { TFunction } from 'i18next'
import { IWorkflowFilterFieldOption, IWorkflowFilterTypeOption } from '@query'
import { formatDate, formatEurosWithOpts } from '@utils'
import { FilterCondition } from './types'
import { getFieldSpec, getFilterTypeSpec, isRelativeDatetimeValue } from './filterConfigUtils'

const formatRelativeValue = (t: TFunction, condition: FilterCondition): string => {
  if (!isRelativeDatetimeValue(condition.value)) return ''

  const { amount, unit } = condition.value
  const unitLabel = t(`workflows.config.filter.relativeUnits.${unit}`, { count: amount })

  return t('workflows.config.filter.relativeAge', { amount, unit: unitLabel })
}

const formatFilterValue = (
  t: TFunction,
  condition: FilterCondition,
  fieldSpec: IWorkflowFilterFieldOption
): string => {
  if (fieldSpec.type === 'boolean') {
    const choice = fieldSpec.choices.find(item => item.value === condition.value)
    return choice?.label ?? String(condition.value)
  }

  if (isRelativeDatetimeValue(condition.value)) {
    return formatRelativeValue(t, condition)
  }

  const rawValue = typeof condition.value === 'string' ? condition.value : ''
  if (!rawValue) return ''

  const date = dayjs(rawValue)
  if (!date.isValid()) return rawValue

  return `${formatDate(date)} ${date.format('HH:mm')}`
}

const getDatetimeConditionClause = (
  t: TFunction,
  condition: FilterCondition,
  fieldSpec: IWorkflowFilterFieldOption
): string => {
  const operator = fieldSpec.operators.find(item => item.value === condition.operator)
  const formattedValue = formatFilterValue(t, condition, fieldSpec)
  const template = isRelativeDatetimeValue(condition.value)
    ? operator?.relative_sentence_template ?? operator?.sentence_template
    : operator?.sentence_template

  if (template) {
    return template.replace('{value}', formattedValue)
  }

  return `${fieldSpec.label}: ${operator?.label ?? condition.operator} ${formattedValue}`.trim()
}

const formatPlainFilterValue = (value: FilterCondition['value']): string => {
  if (Array.isArray(value)) return value.join(', ')
  if (typeof value === 'string' || typeof value === 'number') return String(value)
  return ''
}

const formatAmountValue = (value: FilterCondition['value']): string => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return ''
  return formatEurosWithOpts(value, { inCents: false })
}

const formatAccountLabel = (
  value: FilterCondition['value'],
  accountLabels: Record<number, string> | undefined
): string => {
  if (typeof value !== 'number') return ''
  return accountLabels?.[value] ?? ''
}

const formatContactNames = (
  value: FilterCondition['value'],
  contactLabels: Record<number, string> | undefined,
  separator: string
): string => {
  if (!Array.isArray(value)) return ''
  const names = value
    .filter((item): item is number => typeof item === 'number')
    .map(id => contactLabels?.[id])
    .filter((name): name is string => Boolean(name))
  return names.join(separator)
}

const getPlainConditionClause = (
  condition: FilterCondition,
  fieldSpec: IWorkflowFilterFieldOption,
  accountLabels: Record<number, string> | undefined,
  contactLabels: Record<number, string> | undefined,
  contactSeparator: string
): string => {
  const operator = fieldSpec.operators.find(item => item.value === condition.operator)
  const formattedValue =
    fieldSpec.type === 'account'
      ? formatAccountLabel(condition.value, accountLabels)
      : fieldSpec.type === 'contact'
      ? formatContactNames(condition.value, contactLabels, contactSeparator)
      : fieldSpec.type === 'amount'
      ? formatAmountValue(condition.value)
      : formatPlainFilterValue(condition.value)
  if (
    (fieldSpec.type === 'account' || fieldSpec.type === 'contact' || fieldSpec.type === 'amount') &&
    !formattedValue
  ) {
    return ''
  }
  if (operator?.sentence_template) {
    return operator.sentence_template.replace('{value}', formattedValue)
  }

  return `${fieldSpec.label}: ${operator?.label ?? condition.operator} ${formattedValue}`.trim()
}

const getConditionClause = (
  t: TFunction,
  condition: FilterCondition,
  fieldSpec: IWorkflowFilterFieldOption,
  accountLabels: Record<number, string> | undefined,
  contactLabels: Record<number, string> | undefined
): string => {
  if (
    fieldSpec.type === 'text' ||
    fieldSpec.type === 'account' ||
    fieldSpec.type === 'contact' ||
    fieldSpec.type === 'amount'
  ) {
    return getPlainConditionClause(
      condition,
      fieldSpec,
      accountLabels,
      contactLabels,
      t('workflows.config.filter.joinOr')
    )
  }

  if (fieldSpec.type === 'boolean') {
    const choice = fieldSpec.choices.find(item => item.value === condition.value)
    if (choice?.sentence_template) {
      return choice.sentence_template
    }
    return `${fieldSpec.label}: ${choice?.label ?? String(condition.value)}`
  }

  return getDatetimeConditionClause(t, condition, fieldSpec)
}

export interface FilterSentenceConfig {
  filter_type: string
  match: 'all' | 'any'
  conditions: FilterCondition[]
}

export const buildFilterSentence = (
  t: TFunction,
  config: FilterSentenceConfig,
  catalog: IWorkflowFilterTypeOption[],
  labels?: {
    accountLabels?: Record<number, string>
    contactLabels?: Record<number, string>
  }
): { lead: string; rest: string } | null => {
  if (!config.conditions.length) return null

  const typeSpec = getFilterTypeSpec(catalog, config.filter_type)
  if (!typeSpec) return null

  const lead = typeSpec.sentence_lead
  if (!lead) return null

  const clauses = config.conditions
    .map(condition => {
      const fieldSpec = getFieldSpec(typeSpec, condition.field)
      if (!fieldSpec) return null
      return getConditionClause(
        t,
        condition,
        fieldSpec,
        labels?.accountLabels,
        labels?.contactLabels
      )
    })
    .filter((clause): clause is string => Boolean(clause))

  if (!clauses.length) return null

  const joinKey =
    config.match === 'any' ? 'workflows.config.filter.joinOr' : 'workflows.config.filter.joinAnd'

  return {
    lead,
    rest: clauses.join(t(joinKey))
  }
}
